import React from 'react';
import { FormattedMessage } from 'react-intl';
import RowType from '../../../components/fields/RowType';
import { dateFromYYYYMMDDAndHHMM, overlaps, overlapsDates } from '../../../utils/DateUtils';
import dayjs from '../../../utils/dayjs';
import { isTimeProper } from './validateDateTime';
/** Validate that inner exception rows are not empty */
function validateExceptionInnerRowEmpty(row, innerRow, innerFieldRefs, emptyErrors, _hasError) {
    var _a, _b, _c, _d;
    var hasError = _hasError;
    if (innerRow.startDate === undefined ||
        innerRow.startDate === '' ||
        ((_b = (_a = innerFieldRefs.startDate[row.i]) === null || _a === void 0 ? void 0 : _a[innerRow.i]) === null || _b === void 0 ? void 0 : _b.value) === undefined) {
        emptyErrors.startDate[row.i][innerRow.i] = (React.createElement(FormattedMessage, { id: "stripes-core.label.missingRequiredField" }));
        hasError = true;
    }
    if (innerRow.endDate === undefined ||
        innerRow.endDate === '' ||
        ((_d = (_c = innerFieldRefs.endDate[row.i]) === null || _c === void 0 ? void 0 : _c[innerRow.i]) === null || _d === void 0 ? void 0 : _d.value) === undefined) {
        emptyErrors.endDate[row.i][innerRow.i] = (React.createElement(FormattedMessage, { id: "stripes-core.label.missingRequiredField" }));
        hasError = true;
    }
    if (row.type === RowType.Open) {
        if (innerRow.startTime === undefined) {
            emptyErrors.startTime[row.i][innerRow.i] = (React.createElement(FormattedMessage, { id: "stripes-core.label.missingRequiredField" }));
            hasError = true;
        }
        if (innerRow.endTime === undefined) {
            emptyErrors.endTime[row.i][innerRow.i] = (React.createElement(FormattedMessage, { id: "stripes-core.label.missingRequiredField" }));
            hasError = true;
        }
    }
    return hasError;
}
/** Validate that exception rows as a whole are not empty */
export function validateExceptionsEmpty(rows, innerFieldRefs) {
    var emptyErrors = {
        name: {},
        startDate: {},
        startTime: {},
        endDate: {},
        endTime: {}
    };
    var hasError = false;
    rows.forEach(function (row) {
        if (row.name === undefined || row.name.trim() === '') {
            emptyErrors.name[row.i] = (React.createElement(FormattedMessage, { id: "stripes-core.label.missingRequiredField" }));
            hasError = true;
        }
        emptyErrors.startDate[row.i] = {};
        emptyErrors.startTime[row.i] = {};
        emptyErrors.endDate[row.i] = {};
        emptyErrors.endTime[row.i] = {};
        row.rows.forEach(function (innerRow) {
            hasError = validateExceptionInnerRowEmpty(row, innerRow, innerFieldRefs, emptyErrors, hasError);
        });
    });
    if (hasError) {
        return { empty: emptyErrors };
    }
    return undefined;
}
/** Validate that inner exception rows have proper dates/times */
function validateExceptionInnerRowDatesAndTimes(row, innerRow, innerFieldRefs, invalidErrors, localeDateFormat, localeTimeFormat, _hasError) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var hasError = _hasError;
    if (dayjs(innerRow.startDate).format(localeDateFormat) !==
        ((_b = (_a = innerFieldRefs.startDate[row.i]) === null || _a === void 0 ? void 0 : _a[innerRow.i]) === null || _b === void 0 ? void 0 : _b.value)) {
        invalidErrors.startDate[row.i][innerRow.i] = (React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.error.dateFormat", values: { localeDateFormat: localeDateFormat } }));
        hasError = true;
    }
    if (dayjs(innerRow.endDate).format(localeDateFormat) !==
        ((_d = (_c = innerFieldRefs.endDate[row.i]) === null || _c === void 0 ? void 0 : _c[innerRow.i]) === null || _d === void 0 ? void 0 : _d.value)) {
        invalidErrors.endDate[row.i][innerRow.i] = (React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.error.dateFormat", values: { localeDateFormat: localeDateFormat } }));
        hasError = true;
    }
    if (dayjs(innerRow.startDate).isAfter(innerRow.endDate)) {
        invalidErrors.startDate[row.i][innerRow.i] = (React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.error.dateOrder" }));
        invalidErrors.endDate[row.i][innerRow.i] = (React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.error.dateOrder" }));
        hasError = true;
    }
    if (row.type === RowType.Open) {
        if (!isTimeProper(localeTimeFormat, innerRow.startTime, (_f = (_e = innerFieldRefs.startTime[row.i]) === null || _e === void 0 ? void 0 : _e[innerRow.i]) === null || _f === void 0 ? void 0 : _f.value)) {
            invalidErrors.startTime[row.i][innerRow.i] = (React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.error.timeFormat", values: { localeTimeFormat: localeTimeFormat } }));
            hasError = true;
        }
        if (!isTimeProper(localeTimeFormat, innerRow.endTime, (_h = (_g = innerFieldRefs.endTime[row.i]) === null || _g === void 0 ? void 0 : _g[innerRow.i]) === null || _h === void 0 ? void 0 : _h.value)) {
            invalidErrors.endTime[row.i][innerRow.i] = (React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.error.timeFormat", values: { localeTimeFormat: localeTimeFormat } }));
            hasError = true;
        }
        if (dayjs("".concat(innerRow.startDate, " ").concat(innerRow.startTime)).isAfter("".concat(innerRow.endDate, " ").concat(innerRow.endTime))) {
            invalidErrors.startTime[row.i][innerRow.i] = (React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.error.timeOrder" }));
            invalidErrors.endTime[row.i][innerRow.i] = (React.createElement(FormattedMessage, { id: "ui-calendar.calendarForm.error.timeOrder" }));
            hasError = true;
        }
    }
    return hasError;
}
/** Validate all inner dates/times within each overall row */
export function validateExceptionsDatesAndTimes(rows, innerFieldRefs, localeDateFormat, localeTimeFormat) {
    var invalidErrors = {
        startDate: {},
        startTime: {},
        endDate: {},
        endTime: {}
    };
    var hasError = false;
    rows.forEach(function (row) {
        invalidErrors.startDate[row.i] = {};
        invalidErrors.startTime[row.i] = {};
        invalidErrors.endDate[row.i] = {};
        invalidErrors.endTime[row.i] = {};
        row.rows.forEach(function (innerRow) {
            hasError = validateExceptionInnerRowDatesAndTimes(row, innerRow, innerFieldRefs, invalidErrors, localeDateFormat, localeTimeFormat, hasError);
        });
    });
    if (hasError) {
        return { invalid: invalidErrors };
    }
    return undefined;
}
/** Check overlaps between exception rows */
export function validateExceptionInterOverlaps(rows) {
    var interConflicts = new Set();
    var rowMinMaxes = rows.map(function (row) {
        var _a, _b, _c, _d;
        return ({
            i: row.i,
            startDate: (_b = (_a = dayjs
                .min(row.rows.map(function (_a) {
                var startDate = _a.startDate;
                return dayjs(startDate);
            }))) === null || _a === void 0 ? void 0 : _a.startOf('day')) !== null && _b !== void 0 ? _b : dayjs(0),
            endDate: (_d = (_c = dayjs
                .max(row.rows.map(function (_a) {
                var endDate = _a.endDate;
                return dayjs(endDate);
            }))) === null || _c === void 0 ? void 0 : _c.endOf('day')) !== null && _d !== void 0 ? _d : dayjs(0)
        });
    });
    for (var i = 0; i < rowMinMaxes.length - 1; i++) {
        for (var j = i + 1; j < rowMinMaxes.length; j++) {
            if (overlaps(rowMinMaxes[i].startDate, rowMinMaxes[i].endDate, rowMinMaxes[j].startDate, rowMinMaxes[j].endDate)) {
                interConflicts.add(rowMinMaxes[i].i);
                interConflicts.add(rowMinMaxes[j].i);
            }
        }
    }
    if (interConflicts.size) {
        return { interConflicts: interConflicts };
    }
    return undefined;
}
/** Check overlaps between inner exception rows */
export function getExceptionRowIntraOverlap(row) {
    var overlappingRows = new Set();
    for (var i = 0; i < row.rows.length - 1; i++) {
        for (var j = i + 1; j < row.rows.length; j++) {
            if (overlapsDates(dateFromYYYYMMDDAndHHMM(row.rows[i].startDate, row.rows[i].startTime), dateFromYYYYMMDDAndHHMM(row.rows[i].endDate, row.rows[i].endTime), dateFromYYYYMMDDAndHHMM(row.rows[j].startDate, row.rows[j].startTime), dateFromYYYYMMDDAndHHMM(row.rows[j].endDate, row.rows[j].endTime))) {
                overlappingRows.add(row.rows[i].i);
                overlappingRows.add(row.rows[j].i);
            }
        }
    }
    return overlappingRows;
}
/** Validate overlaps between inside exception rows */
export function validateExceptionIntraOverlaps(rows) {
    var intraConflicts = {};
    var hasError = false;
    rows.forEach(function (row) {
        if (row.type === RowType.Closed)
            return;
        var innerOverlaps = getExceptionRowIntraOverlap(row);
        if (innerOverlaps.size) {
            intraConflicts[row.i] = new Set();
            hasError = true;
            innerOverlaps.forEach(function (innerI) {
                intraConflicts[row.i].add(innerI);
            });
        }
    });
    if (hasError) {
        return { intraConflicts: intraConflicts };
    }
    return undefined;
}
/** Validate all parts of exceptions */
export default function validateExceptions(rows, innerFieldRefs, localeDateFormat, localeTimeFormat) {
    if (rows === undefined) {
        return {};
    }
    var emptyErrors = validateExceptionsEmpty(rows, innerFieldRefs);
    if (emptyErrors !== undefined) {
        return { exceptions: emptyErrors };
    }
    var invalidErrors = validateExceptionsDatesAndTimes(rows, innerFieldRefs, localeDateFormat, localeTimeFormat);
    if (invalidErrors !== undefined) {
        return { exceptions: invalidErrors };
    }
    var interOverlaps = validateExceptionInterOverlaps(rows);
    if (interOverlaps !== undefined) {
        return { exceptions: interOverlaps };
    }
    return {
        // can be undefined but that is acceptable here
        // as no other cases to check
        exceptions: validateExceptionIntraOverlaps(rows)
    };
}
